import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { motion, useMotionValue, animate, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Trophy, Users, Zap, ChevronDown, Lock, BarChart2, MessageCircle, Target, Star, Calendar, IdCard, TrendingUp, Swords, Award, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Config, Player, Match, Team } from '../../types';
import { PlayerCardPremium } from '../player/PlayerCardPremium';
import { FadeInImage } from '../common/FadeInImage';

interface GuestLandingProps {
  config: Config;
  players: Player[];
  onRegisterClick: () => void;
  onLoginClick: () => void;
  sponsorsCarousel?: React.ReactNode;
  topComponent?: React.ReactNode;
}

/* ─── Animated Counter ─────────────────────────────────────────── */
function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const duration = 1800;
      const startTime = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - startTime) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(Math.floor(ease * target));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <div ref={ref}>{val.toLocaleString()}{suffix}</div>;
}

/* ─── Player Card ──────────────────────────────────────────────── */
const PlayerCard = React.memo(function PlayerCard({ player, config, allPlayers }: { player: Player; config: Config; allPlayers: Player[] }) {
  const [stats, setStats] = React.useState<{ matchesPlayed: number, wins: number, losses: number, winRate: number }>({
    matchesPlayed: 0, wins: 0, losses: 0, winRate: 0
  });

  React.useEffect(() => {
    let unsubscribe = () => {};
    import('firebase/firestore').then(({ doc, onSnapshot }) => {
      import('../../lib/firebase').then(({ db, getCollectionPath }) => {
        unsubscribe = onSnapshot(
          doc(db, getCollectionPath('playerStats'), player.id),
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              const matchesPlayed = data.totalCompletedMatches || 0;
              const wins = data.cumulativeWins || 0;
              const losses = Math.max(0, matchesPlayed - wins);
              const winRate = matchesPlayed > 0 ? (wins / matchesPlayed) * 100 : 0;
              
              setStats({
                matchesPlayed,
                wins,
                losses,
                winRate
              });
            }
          },
          (error) => {
            // Silently ignore permission errors — playerStats is public (allow read: if true)
            // but may briefly fail during Firebase Auth initialization.
            if (error.code !== 'permission-denied') {
              console.error('PlayerCard stats error:', error);
            }
          }
        );
      });
    });
    return () => unsubscribe();
  }, [player.id]);

  const categoryRank = React.useMemo(() => {
    const globalPlayers = allPlayers
      .filter(p => p.isValidated && !p.isGuest)
      .sort((a, b) => (b.points || 0) - (a.points || 0));
    return globalPlayers.findIndex(p => p.id === player.id) + 1;
  }, [player.id, allPlayers]);

  const scale = 0.5;
  const width = 340 * scale;
  const height = width * (4 / 3);

  // We mock a Match[] array with the length of matchesPlayed since PlayerCardPremium expects it
  const dummyMatches = React.useMemo(() => new Array(stats.matchesPlayed).fill({}), [stats.matchesPlayed]);

  return (
    <div
      className="relative flex-shrink-0 group hover:scale-[1.03] transition-transform duration-300 z-10 hover:z-20 cursor-pointer"
      style={{ width: `${width}px`, height: `${height}px`, userSelect: 'none' }}
    >
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: '340px' }} className="pointer-events-none">
        <PlayerCardPremium
          player={player}
          playerMatches={dummyMatches as any}
          wins={stats.wins}
          losses={stats.losses}
          winRate={stats.winRate}
          categoryRank={categoryRank}
          config={config}
          isEnRacha={player.isOnFire || false}
          hideShadow={true}
        />
      </div>
    </div>
  );
});

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  desc: string;
  tag?: string;
}

interface FeatureCardProps {
  f: FeatureItem;
  i: number;
  primary: string;
}

const FeatureCard = React.memo(function FeatureCard({ f, i, primary }: FeatureCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(cardRef, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40, scale: 0.8 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 3.0, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
      className="relative p-5 sm:p-6 rounded-2xl border border-white/10 overflow-hidden group hover:border-white/30 hover:-translate-y-2 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] flex flex-col items-center text-center hover:bg-white/[0.1] hover:shadow-[0_15px_35px_-10px_rgba(0,0,0,0.6)]"
      style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(8px)' }}
    >
      {/* Glow hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 50%, ${primary}12, transparent 70%)` }} />

      <div
        className="relative mb-4 w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
        style={{
          background: `${primary}18`,
          color: primary,
          boxShadow: `0 4px 12px rgba(0,0,0,0.15), 0 0 0 1px ${primary}10`
        }}
      >
        {/* Luminous sweep — fires 5s after card enters viewport */}
        <motion.span
          initial={{ x: '-150%', skewX: '-20deg' }}
          animate={isInView ? { x: '150%' } : { x: '-150%' }}
          transition={{ delay: 2.0 + i * 0.1, duration: 1.2, ease: 'easeInOut' }}
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), rgba(255,255,255,0.18), rgba(255,255,255,0.08), transparent)',
          }}
        />
        {f.icon}
      </div>
      <h3 className="font-black text-white text-[11px] sm:text-[12px] mb-2 leading-tight uppercase tracking-wider">{f.title}</h3>
      <p className="text-white/50 text-[10px] sm:text-xs leading-relaxed max-w-[240px]">{f.desc}</p>
      {f.tag && (
        <span className="mt-3 text-[6px] sm:text-[7px] font-black uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shadow-[0_0_8px_rgba(234,179,8,0.12)]">
          {f.tag}
        </span>
      )}
    </motion.div>
  );
});

/* ─── Main Component ───────────────────────────────────────────── */
export const GuestLanding = React.memo(function GuestLanding({ config, players, onRegisterClick, onLoginClick, sponsorsCarousel, topComponent }: Omit<GuestLandingProps, 'matches' | 'teams'>) {
  const primary = config.primaryColor || '#eab308';
  const { scrollY } = useScroll();
  const scrollOpacity = useTransform(scrollY, [0, 150], [0.8, 0]);
  const validatedPlayers = players
    .filter(p => p.isValidated && !p.isGuest)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, 9);
  const totalCardsCount = validatedPlayers.length + 1; // 9 players + 1 locked card


  /* ── Drag carousel ── */
  const carouselRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const cardW = 186; // width (170px) + gap-4 (16px)
  const maxDrag = -(Math.max(totalCardsCount - 2, 0) * cardW);
  const [hasDragged, setHasDragged] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);

  const [statsSlide, setStatsSlide] = useState(0);
  const [isStatsInView, setIsStatsInView] = useState(false);
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(true);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = statsRef.current;
    if (!el || isStatsInView) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setIsStatsInView(true); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isStatsInView]);

  useEffect(() => {
    if (!isStatsInView || !isAutoPlayActive) return;
    const interval = setInterval(() => {
      setStatsSlide(s => (s + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, [isStatsInView, isAutoPlayActive]);

  useEffect(() => {
    if (hasDragged || isHovered) return;
    // Rango sutil [-95, 5] con una duración ampliada a 3.6s para que la oscilación sea extremadamente
    // suave, lenta y majestuosa, flotando delicadamente.
    const controls = animate(x, [-95, 5], {
      repeat: Infinity,
      repeatType: "reverse",
      duration: 3.6, // Incrementado a 3.6s para un movimiento súper lento y sedoso
      ease: "easeInOut"
    });
    return () => controls.stop();
  }, [x, hasDragged, isHovered]);

  const snapToNearest = useCallback(() => {
    const cur = x.get();
    const velocity = x.getVelocity();

    // Calculate targeted position incorporating user's swipe speed (momentum)
    let targetIndex = Math.round(-cur / cardW);

    // If user swiped quickly (velocity > 150px/sec), project momentum into the target index
    if (Math.abs(velocity) > 150) {
      // projecting 0.12 seconds of movement based on momentum
      targetIndex = Math.round((-cur + (velocity * 0.12)) / cardW);
    }

    const clamped = Math.max(0, Math.min(targetIndex, totalCardsCount - 2));

    // Animate to target card with a ultra-smooth custom spring that inherits the swipe's entry velocity
    animate(x, -(clamped * cardW), {
      type: 'spring',
      stiffness: 140, // Reduced from 200 for a more elegant, buttery transition
      damping: 24,    // Tuned damping ratio for 0% bounce-back jitter
      mass: 0.5,      // Lighter weight for ultra-responsive feel
      velocity: velocity // Pass current momentum velocity into the spring solver
    });
  }, [x, cardW, totalCardsCount]);

  const features = [
    { icon: <BarChart2 size={22} />, title: 'Ranking local', desc: 'Tu posición actualizada tras cada torneo o partido oficial.' },
    { icon: <Trophy size={22} />, title: 'Torneos Exclusivos autogestionados', desc: 'Con sistema de coordinación y chats privados.' },
    { icon: <Users size={22} />, title: 'Red social / comunidad', desc: 'Encontrá a tus amigos en la app, para coordinar tu próximo partido.' },
    { icon: <Calendar size={22} />, title: 'Reserva de turnos', desc: 'Elegí tu cancha, horario favorito, y reservá.', tag: 'Coordinación en tiempo real' },
    { icon: <Target size={22} />, title: 'Categorías y validaciones', desc: 'Siempre jugarás torneos y partidos acorde a tu nivel.' },
    { icon: <IdCard size={22} />, title: 'Player card', desc: 'Al registrarte ya conseguís tu player card oficial de RPT lista para compartir.' },
  ];

  return (
    <div className="w-full flex flex-col">
      {topComponent}

      {/* ══════════════════════════════════════════
          FASE 1 — HERO con video / fondo
      ══════════════════════════════════════════ */}
      {!topComponent && (
        <section
          className="relative w-screen flex flex-col items-center justify-center"
        style={{
          minHeight: '85svh',
          marginLeft: 'calc(-50vw + 50%)',
          marginTop: '-5rem',
        }}
      >


        {/* Animated Background Ambient Lights (Volumetric Orbs) */}
        <div
          className="absolute -inset-x-32 inset-y-0 pointer-events-none z-[2]"
          style={{
            maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)'
          }}
        >
          {/* Orb 1: Primary theme color, floats top-left to center */}
          <motion.div
            animate={{
              x: [-120, 120, -120],
              y: [-80, 100, -80],
              scale: [1, 1.25, 1],
              opacity: [0.2, 0.45, 0.2],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute w-[320px] h-[320px] rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${primary} 0%, transparent 70%)`,
              filter: 'blur(60px)',
              left: '15%',
              top: '20%',
              willChange: 'transform, opacity',
            }}
          />

          {/* Orb 2: Gold/Amber, floats bottom-right to center-right */}
          <motion.div
            animate={{
              x: [150, -150, 150],
              y: [100, -100, 100],
              scale: [1.2, 0.9, 1.2],
              opacity: [0.15, 0.38, 0.15],
            }}
            transition={{
              duration: 14,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1,
            }}
            className="absolute w-[350px] h-[350px] rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(234,179,8,0.9) 0%, transparent 70%)',
              filter: 'blur(65px)',
              right: '20%',
              bottom: '15%',
              willChange: 'transform, opacity',
            }}
          />

          {/* Orb 3: White/Soft Blue, floats center-bottom to center-left */}
          <motion.div
            animate={{
              x: [-100, 100, -100],
              y: [80, -80, 80],
              scale: [0.9, 1.15, 0.9],
              opacity: [0.12, 0.28, 0.12],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2,
            }}
            className="absolute w-[280px] h-[280px] rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%)',
              filter: 'blur(55px)',
              left: '40%',
              bottom: '25%',
              willChange: 'transform, opacity',
            }}
          />
        </div>


        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col items-center text-center px-4 max-w-4xl mx-auto"
          animate={{
            scale: [1, 1.06],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut',
            delay: 0.8,
          }}
          style={{ willChange: 'transform' }}
        >
          {/* Logo */}
          {config.logoUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative w-24 h-24 md:w-28 md:h-28 mb-6 drop-shadow-2xl shrink-0"
            >
              {/* Main Logo Image */}
              <FadeInImage
                  src={config.logoUrl}
                  alt="logo"
                  className="w-full h-full object-contain"
                  noPlaceholderBg={true}
                  onLoad={() => setIsLogoLoaded(true)}
                />

              {/* Metallic Sweep Shine Overlay */}
              <AnimatePresence>
                {config.logoShineEffect && isLogoLoaded && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.0, ease: "easeInOut" }}
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none overflow-hidden z-10"
                    style={{
                      maskImage: `url(${config.logoUrl})`,
                      WebkitMaskImage: `url(${config.logoUrl})`,
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      WebkitMaskPosition: 'center',
                    }}
                  >
                    <div
                      className="logo-shine-bar"
                      style={{
                        position: 'absolute',
                        top: '-20%',
                        left: 0,
                        width: '40%',
                        height: '140%',
                        background: 'linear-gradient(105deg, transparent 10%, rgba(255,255,255,0.7) 45%, rgba(255,255,255,0.25) 62%, transparent 85%)',
                        filter: 'blur(3px)',
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-2 px-3 py-1 rounded-full border mb-6 text-[9px] font-black uppercase tracking-[0.3em]"
            style={{ borderColor: 'rgba(212, 175, 55, 0.35)', background: 'rgba(212, 175, 55, 0.08)', color: '#d4af37' }}
          >
            <Zap size={9} fill="#d4af37" className="text-[#d4af37]" /> Tu app de padel y gestión de clubes
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-black uppercase italic tracking-tighter text-white leading-none mb-4"
            style={{ fontSize: 'clamp(2.5rem, 8vw, 5.5rem)', textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}
          >
            {config.guestHeroTitle || 'Únete a la'}{' '}
            <span style={{ color: primary, filter: `drop-shadow(0 0 20px ${primary}60)` }}>
              {config.guestHeroTitleAccent || 'Élite'}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white/60 text-base md:text-lg max-w-xl mb-10 font-medium leading-relaxed"
          >
            {config.guestHeroSubtitle || `La plataforma donde se juegan los mejores partidos de ${config.pageName || 'pádel'}.`}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 items-center"
          >
            {/* Primary CTA */}
            <button
              onClick={onRegisterClick}
              className="relative group px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95"
              style={{ background: primary, color: '#000', boxShadow: `0 0 30px ${primary}60, 0 4px 15px rgba(0,0,0,0.3)` }}
            >
              {/* Shine sweep */}
              <span className="absolute inset-0 translate-x-[-110%] group-hover:translate-x-[110%] transition-transform duration-700 ease-out"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', skewX: '-15deg' }} />
              ✦ Crear mi Perfil Gratis
            </button>

            {/* Secondary CTA */}
            <button
              onClick={onLoginClick}
              className="px-6 py-4 rounded-2xl font-bold text-sm border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-all duration-300 backdrop-blur-sm bg-white/5"
            >
              Ya tengo cuenta
            </button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
          style={{ opacity: scrollOpacity }}
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
        >
          <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-white">Explorá</span>
          <ChevronDown size={16} className="text-yellow-400" />
        </motion.div>
      </section>
      )}

      {/* ══════════════════════════════════════════
          SPONSORS (Escritorio)
      ══════════════════════════════════════════ */}
      {sponsorsCarousel && (
        <section className="w-full relative z-20">
          {sponsorsCarousel}
        </section>
      )}

      {/* ══════════════════════════════════════════
          FASE 4 — Bento Grid de Features (Movido arriba)
      ══════════════════════════════════════════ */}
      <section className="py-14 border-t border-white/5" style={{ contain: 'layout style' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white">
            Beneficios de <span className="text-yellow-400">registrarme</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-4xl mx-auto">
          {features.map((f, i) => (
            <FeatureCard key={f.title} f={f} i={i} primary={primary} />
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FASE 3 — Carrusel 3D de Player Cards (Movido arriba)
      ══════════════════════════════════════════ */}
      {validatedPlayers.length > 0 && config.guestShowPlayerCarousel !== false && (
        <section className="py-14 border-t border-white/5 overflow-hidden" style={{ contain: 'layout style' }}>
          <div className="text-center mb-10">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] mb-3" style={{ color: primary }}>Comunidad</p>
            <h2 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white">
              Conocé a los <span style={{ color: primary }}>Jugadores</span>
            </h2>
            <div className="mt-4 flex justify-center">
              <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.2em] font-black text-white/60 border border-white/10 px-4 py-1.5 rounded-full bg-white/5 backdrop-blur-sm shadow-sm">
                Registrate para ver el ranking completo
              </span>
            </div>
          </div>

          {/* Drag carousel */}
          <div
            className="relative overflow-visible py-4 -my-4"
            ref={carouselRef}
            style={{
              maskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)'
            }}
          >
            <motion.div
              className="flex gap-4 px-8 cursor-grab active:cursor-grabbing select-none"
              style={{ x }}
              drag="x"
              dragConstraints={{ left: maxDrag, right: 0 }}
              dragElastic={0.35} // Increased from 0.2 for a more elastic, responsive boundary pull
              dragMomentum={false} // Disable default physics so our customized high-fidelity momentum spring takes full control
              onDragStart={() => setHasDragged(true)}
              onHoverStart={() => setIsHovered(true)}
              onHoverEnd={() => setIsHovered(false)}
              onDragEnd={snapToNearest}
              whileTap={{ cursor: 'grabbing' }}
            >
              {validatedPlayers.map((player) => (
                <React.Fragment key={player.id}>
                  <PlayerCard player={player} config={config} allPlayers={players} />
                </React.Fragment>
              ))}

              {/* The 10th Card: Beautifully blurred Locked CTA Card */}
              <div
                onClick={onRegisterClick}
                className="relative flex-shrink-0 w-[170px] rounded-[24px] overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-300 flex flex-col items-center justify-center text-center p-4 cursor-pointer group shadow-[0_15px_30px_rgba(0,0,0,0.5)] select-none shrink-0"
                style={{ height: '226.6px' }}
              >
                {/* Glassmorphic Base Gradient: Much lighter, matching the premium theme with high transparency */}
                <div
                  className="absolute inset-0 z-0"
                  style={{
                    background: `radial-gradient(circle at 50% 30%, ${primary}22 0%, rgba(255,255,255,0.06) 60%, rgba(255,255,255,0.02) 100%)`
                  }}
                />

                {/* Frosted Glass blur overlay (transparent and luminous) */}
                <div className="absolute inset-0 bg-white/[0.03] backdrop-blur-md z-0" />

                {/* Neon accent boundary glow */}
                <div className="absolute inset-0 border-[2px] border-dashed rounded-[24px] opacity-30 group-hover:opacity-60 transition-opacity duration-300 pointer-events-none" style={{ borderColor: primary }} />

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center h-full w-full">
                  {/* Locked padlock icon */}
                  <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-3 group-hover:scale-110 group-hover:bg-white/10 transition-all duration-300" style={{ color: primary }}>
                    <Lock size={18} className="animate-pulse" />
                  </div>

                  {/* Locked Title */}
                  <h3 className="text-white font-black text-xs uppercase tracking-wider mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    ¿Querés ver más?
                  </h3>

                  {/* Subtext */}
                  <p className="text-white/40 text-[8.5px] leading-tight px-1 mb-4">
                    Registrate para ver el ranking completo de jugadores
                  </p>

                  {/* Mini Register CTA Button */}
                  <div
                    className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all duration-300 group-hover:scale-105 active:scale-95 shadow-lg"
                    style={{ background: primary, color: '#000' }}
                  >
                    Registrarme
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA bajo el carrusel */}
          <div className="flex justify-center mt-10">
            <button
              onClick={onRegisterClick}
              className="group flex items-center gap-2 px-6 py-3 rounded-xl border text-sm font-bold transition-all duration-300 hover:scale-105"
              style={{ borderColor: `${primary}50`, color: primary, background: `${primary}10` }}
            >
              Creá tu propia player card
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </button>
          </div>
        </section>
      )}





      {/* ══════════════════════════════════════════
          FASE 5 — Sección Bloqueada (FOMO) — Stats Carousel
      ══════════════════════════════════════════ */}
      <section className="py-14 border-t border-white/5 overflow-hidden" style={{ contain: 'layout style' }}>
        <div
          className="text-center mb-8"
          ref={statsRef}
        >
          <p className="text-[10px] font-black uppercase tracking-[0.4em] mb-3 opacity-40 text-white">Contenido Exclusivo</p>
          <h2 className="text-3xl md:text-4xl font-black uppercase italic tracking-tighter text-white">
            Tus estadísticas <span style={{ color: primary }}>avanzadas</span>
          </h2>
        </div>

        {/* Stats Carousel */}
        {(() => {
          const totalSlides = 3;
          const goNext = () => {
            setIsAutoPlayActive(false);
            setStatsSlide(s => (s + 1) % totalSlides);
          };
          const goPrev = () => {
            setIsAutoPlayActive(false);
            setStatsSlide(s => (s - 1 + totalSlides) % totalSlides);
          };

          // Mock winrate data
          const mockWinRate = [42, 48, 45, 52, 55, 50, 58, 62, 60, 65, 63, 68, 66, 70, 72, 69, 74, 71, 75, 73];
          const mockChartPath = (() => {
            const pts = mockWinRate.map((r, i) => ({ x: (i / (mockWinRate.length - 1)) * 100, y: 100 - r }));
            let d = `M ${pts[0].x} ${pts[0].y}`;
            for (let i = 0; i < pts.length - 1; i++) {
              const curr = pts[i]; const next = pts[i + 1];
              const cp1x = curr.x + (next.x - curr.x) / 3;
              const cp2x = curr.x + (next.x - curr.x) * 2 / 3;
              d += ` C ${cp1x} ${curr.y}, ${cp2x} ${next.y}, ${next.x} ${next.y}`;
            }
            return d;
          })();
          const mockFillPath = mockChartPath + ' L 100 100 L 0 100 Z';

          return (
            <div className="relative max-w-4xl mx-auto">
              {/* Navigation arrows */}
              <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-slate-900/70 border border-white/20 flex items-center justify-center hover:bg-slate-800 transition-all backdrop-blur-sm">
                <ChevronLeft size={16} className="text-white/70" />
              </button>
              <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-8 h-8 rounded-full bg-slate-900/70 border border-white/20 flex items-center justify-center hover:bg-slate-800 transition-all backdrop-blur-sm">
                <ChevronRight size={16} className="text-white/70" />
              </button>

              {/* Slides container */}
              <div className="overflow-hidden rounded-2xl mx-10">
                <motion.div
                  className="flex"
                  animate={{ x: `${-statsSlide * 100}%` }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                  {/* ── SLIDE 1: Evolución de Winrate ── */}
                  <div className="w-full flex-shrink-0 px-2">
                    <div className="bg-slate-900/60 rounded-[2rem] p-6 md:p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent pointer-events-none" />
                      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 relative z-10">
                        <div className="flex flex-col gap-1 text-center md:text-left">
                          <span className="text-[10px] font-black uppercase text-white/40 tracking-[0.4em]">Trajectory Analysis</span>
                          <h3 className="text-lg font-black italic text-white uppercase tracking-tighter">Evolución de <span style={{ color: primary }}>Winrate</span></h3>
                        </div>
                        <div className="flex items-center gap-2 px-2 py-1 bg-black/60 rounded-full border border-white/15 backdrop-blur-md">
                          <div className="w-1.5 h-1.5 bg-[#00ffcc] rounded-full animate-pulse shadow-[0_0_10px_rgba(0,255,204,0.6)]" />
                          <span className="text-[8px] font-black uppercase text-[#00ffcc] tracking-widest italic">live tracking</span>
                        </div>
                      </div>
                      <div className="relative min-h-[200px] md:min-h-[260px] z-10">
                        {/* Y Axis */}
                        <div className="absolute inset-0 flex flex-col justify-between z-0 opacity-50">
                          {[100, 75, 50, 25, 0].map(v => (
                            <div key={v} className="w-full flex items-center gap-4">
                              <span className="text-[8px] font-black text-white/20 w-8">{v}%</span>
                              <div className="flex-1 h-[1px] bg-white/[0.08]" />
                            </div>
                          ))}
                        </div>
                        {/* Chart */}
                        <div className="absolute inset-0 left-12 right-4 bottom-0">
                          <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <defs>
                              <linearGradient id="mockChartFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.35" />
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <motion.path
                              key={`winrate-line-${statsSlide}`}
                              initial={{ pathLength: 0, opacity: 0 }}
                              animate={statsSlide === 0 ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
                              transition={{ duration: 2.5, ease: 'easeOut' }}
                              d={mockChartPath}
                              fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round"
                              className="drop-shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                            />
                            <motion.path
                              key={`winrate-fill-${statsSlide}`}
                              initial={{ opacity: 0 }}
                              animate={statsSlide === 0 ? { opacity: 1 } : { opacity: 0 }}
                              transition={{ duration: 1.5, delay: 1 }}
                              d={mockFillPath}
                              fill="url(#mockChartFill)"
                            />
                          </svg>
                        </div>
                      </div>
                      {/* Bottom stats mini row */}
                      <div className="flex justify-center gap-8 mt-4 relative z-10">
                        {[{ label: 'Winrate Actual', val: '73%' }, { label: 'Mejor Racha', val: '7W' }, { label: 'Tendencia', val: '↑ +5%' }].map(s => (
                          <div key={s.label} className="text-center">
                            <p className="text-lg font-black italic text-white">{s.val}</p>
                            <p className="text-[7px] font-bold uppercase text-white/30 tracking-widest">{s.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── SLIDE 2: Análisis Táctico Avanzado ── */}
                  <div className="w-full flex-shrink-0 px-2">
                    <div className="bg-slate-900/60 rounded-[2rem] p-6 md:p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-6 opacity-5">
                        <Swords size={80} style={{ color: primary }} />
                      </div>
                      <h4 className="text-[10px] font-black uppercase text-white/40 tracking-[0.4em] mb-8">Análisis Táctico Avanzado</h4>
                      <div className="space-y-6">
                        {/* Mentalidad Ganadora */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-xl bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 shadow-[0_0_15px_rgba(234,179,8,0.1)]">
                                <Zap size={15} />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Mentalidad Ganadora</span>
                                <span className="text-[8px] font-bold text-yellow-400/90 tracking-wide">Capacidad de remontar</span>
                              </div>
                            </div>
                            <span className="text-lg font-black italic text-yellow-400 leading-none">67%</span>
                          </div>
                          <p className="text-[9px] text-white/40 leading-relaxed">
                            % de victorias tras perder el 1er set. Evalúa tu fortaleza mental para remontar.
                          </p>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                            <motion.div
                              key={`tactical-yellow-${statsSlide}`}
                              initial={{ width: "0%" }}
                              animate={statsSlide === 1 ? { width: "67%" } : { width: "0%" }}
                              transition={{ duration: 1.5, ease: "easeOut" }}
                              className="h-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.3)]"
                            />
                          </div>
                          <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                            <span className="text-yellow-400/70">4 de 6 ganados</span>
                          </div>
                        </div>

                        {/* Control bajo Presión */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-xl bg-[#bef264]/10 text-[#bef264] border border-[#bef264]/20 shadow-[0_0_15px_rgba(190,242,100,0.1)]">
                                <Target size={15} />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Control bajo Presión</span>
                                <span className="text-[8px] font-bold text-[#bef264]/90 tracking-wide">Frialdad en tie-breaks</span>
                              </div>
                            </div>
                            <span className="text-lg font-black italic text-[#bef264] leading-none">75%</span>
                          </div>
                          <p className="text-[9px] text-white/40 leading-relaxed">
                            Efectividad en tie-breaks (6-6) y sets cerrados. Muestra tu frialdad bajo máxima tensión.
                          </p>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                            <motion.div
                              key={`tactical-green-${statsSlide}`}
                              initial={{ width: "0%" }}
                              animate={statsSlide === 1 ? { width: "75%" } : { width: "0%" }}
                              transition={{ duration: 1.5, ease: "easeOut" }}
                              className="h-full bg-[#bef264] shadow-[0_0_10px_rgba(190,242,100,0.3)]"
                            />
                          </div>
                          <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                            <span className="text-[#bef264]/70">6 de 8 ganados</span>
                          </div>
                        </div>

                        {/* Intensidad de Juego */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.15)]">
                                <TrendingUp size={15} />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">Intensidad de Juego</span>
                                <span className="text-[8px] font-bold text-blue-400/90 tracking-wide">Exigencia competitiva</span>
                              </div>
                            </div>
                            <span className="text-lg font-black italic text-blue-400 leading-none">9.4</span>
                          </div>
                          <p className="text-[9px] text-white/40 leading-relaxed">
                            Promedio de games por set. Un valor alto (+9.0) indica máxima paridad y exigencia física.
                          </p>
                          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden border border-white/5">
                            <motion.div
                              key={`tactical-blue-${statsSlide}`}
                              initial={{ width: "0%" }}
                              animate={statsSlide === 1 ? { width: "78%" } : { width: "0%" }}
                              transition={{ duration: 1.5, ease: "easeOut" }}
                              className="h-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                            />
                          </div>
                          <div className="text-center text-[8px] font-bold uppercase tracking-widest">
                            <span className="text-blue-400/70">9.4 games por set</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── SLIDE 3: Analytics Hub ── */}
                  <div className="w-full flex-shrink-0 px-2">
                    <div className="bg-slate-900/60 rounded-[2rem] p-6 md:p-8 border border-white/10 relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent pointer-events-none" />
                      <h4 className="text-[10px] font-black uppercase text-white/40 tracking-[0.4em] mb-6 flex items-center justify-center gap-2 relative z-10">
                        <Award size={14} style={{ color: primary }} /> Analytics Hub
                      </h4>
                      <div className="grid grid-cols-2 gap-4 relative z-10">
                        {/* Winrate Global */}
                        <div className="bg-slate-950/60 rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-center text-center">
                          <span className="text-[7px] font-black uppercase text-white/30 tracking-[0.2em] block mb-1">Winrate Global</span>
                          <div className="flex items-baseline gap-1 mb-2">
                            <span className="text-3xl font-black italic text-white">73</span>
                            <span className="text-xs font-black uppercase" style={{ color: primary }}>%</span>
                          </div>
                          <div className="w-full h-1.5 bg-white/15 rounded-full overflow-hidden border border-white/5">
                            <motion.div key={`global-winrate-${statsSlide}`} initial={{ width: '0%' }} animate={statsSlide === 2 ? { width: '73%' } : { width: '0%' }} transition={{ duration: 1.2, ease: 'easeOut' }} className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]" />
                          </div>
                        </div>
                        {/* Efectividad en games */}
                        <div className="bg-slate-950/60 rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-center text-center">
                          <span className="text-[7px] font-black uppercase tracking-widest mb-1" style={{ color: primary }}>Efectividad en games</span>
                          <span className="text-3xl font-black italic text-white leading-none">64%</span>
                          <div className="w-full flex flex-col gap-1 mt-2">
                            <div className="h-1.5 w-full bg-white/15 rounded-full overflow-hidden border border-white/5">
                              <motion.div key={`games-eff-${statsSlide}`} initial={{ width: '0%' }} animate={statsSlide === 2 ? { width: '64%' } : { width: '0%' }} transition={{ duration: 1.2 }} className="h-full bg-emerald-500" />
                            </div>
                            <div className="flex justify-between text-[7px] font-black italic px-1 opacity-60">
                              <span className="text-emerald-400">218W</span>
                              <span className="text-red-400">122L</span>
                            </div>
                          </div>
                        </div>
                        {/* Forma Reciente */}
                        <div className="bg-slate-950/60 rounded-2xl p-4 border border-white/10 flex flex-col items-center justify-center gap-2 overflow-hidden">
                          <div className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity" title="De izquierda (más antiguo) a derecha (más reciente)">
                            <span className="text-[7px] font-black uppercase text-white/50 tracking-[0.3em] italic">Forma</span>
                            <div className="flex items-center">
                              <div className="w-6 h-[1.5px] bg-white/40" />
                              <ArrowRight size={12} className="-ml-1.5 text-white/40" />
                            </div>
                          </div>
                          <div className="flex gap-1 justify-center flex-wrap">
                            {['W', 'W', 'L', 'W', 'W', 'W', 'L', 'W'].map((r, i) => {
                              const isLatest = i === 7;
                              return (
                                <div key={i} className={`relative w-4 h-4 sm:w-5 sm:h-5 rounded border flex items-center justify-center text-[7px] sm:text-[8px] font-black shrink-0 ${r === 'W' ? 'border-emerald-500/20 text-emerald-400 bg-emerald-500/5' : 'border-red-500/20 text-red-400 bg-red-500/5'} ${isLatest ? 'scale-105 z-10 shadow-[0_0_10px_rgba(255,255,255,0.15)] ring-1 ring-white/20 animate-[pulse_1s_infinite]' : ''}`}>
                                  {r}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {/* Rachas */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-slate-950/60 rounded-2xl p-3 border border-white/10 flex flex-col items-center text-center">
                            <p className="text-[7px] font-black uppercase text-white/30 tracking-widest mb-1">Racha Actual</p>
                            <p className="text-xl font-black italic text-blue-400">5W</p>
                          </div>
                          <div className="bg-slate-950/60 rounded-2xl p-3 border border-white/10 flex flex-col items-center text-center">
                            <p className="text-[7px] font-black uppercase text-white/30 tracking-widest mb-1">Racha Máxima</p>
                            <p className="text-xl font-black italic" style={{ color: primary }}>7W</p>
                          </div>
                        </div>
                      </div>
                      {/* Logros mini */}
                      <div className="mt-6 relative z-10">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <div className="p-1.5 rounded-lg bg-theme-primary/10 text-theme-primary-text"><Award size={12} style={{ color: primary }} /></div>
                          <span className="text-[9px] font-black uppercase tracking-widest text-white/40">Logros y Trofeos</span>
                        </div>
                        <div className="flex gap-2 justify-center">
                          {['🥇 Campeón 3ra Cat.', '🥈 Sub-campeón Apertura', '🔥 Racha x7'].map((ach, i) => (
                            <div key={i} className="px-3 py-2 rounded-xl bg-slate-950/60 border border-white/10 text-center">
                              <p className="text-[8px] font-black uppercase text-white/60">{ach}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStatsSlide(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === statsSlide ? 'w-6' : 'bg-white/20'}`}
                    style={i === statsSlide ? { background: primary } : {}}
                  />
                ))}
              </div>

            </div>
          );
        })()}
      </section>


    </div>
  );
});
